<template>
    <div class="container">
        <h1 class="secondary-title">
            {{ $t('email.auth.createModal.title', { provider: providerName }) }}
        </h1>

        <section>
            <ol class="instructions">
                <li>{{ $t('email.auth.createModal.instructions.steps.one', { provider: providerName }) }}</li>

                <li>{{ $t('email.auth.createModal.instructions.steps.two') }}</li>
            </ol>

            <div v-if="isCustomPrefixSelectorEnabled" class="dkim-selector-card-container">
                <DsCard elevation="z0">
                    <DsCollapsible class="custom-dkim-collapsible" :is-open="collapsibleOpen">
                        <template #header>
                            <div
                                class="collapsible-header-container"
                                @click="toggleCollapsible"
                            >
                                <div as="span" variant="body">
                                    {{ $t('email.auth.createModal.prefixSelector.header') }}
                                </div>

                                <DsIcon :name="chevronIcon" />
                            </div>
                        </template>

                        <template #default>
                            <div>
                                <div as="span" variant="body-2">
                                    {{ $t('email.auth.createModal.prefixSelector.body') }}
                                </div>

                                <div class="input-field-container">
                                    <DsInputField
                                        v-model="appId"
                                        name="helpText"
                                        :label="$t('email.auth.createModal.prefixSelector.inputFields.fieldLabelAppId')"
                                        readonly
                                    >
                                        <template #help>
                                            {{ $t('email.auth.createModal.prefixSelector.inputFields.helpAppId') }}
                                        </template>
                                    </DsInputField>

                                    <DsInputField
                                        v-model="customPrefixInput"
                                        name="helpText"
                                        :label="$t('email.auth.createModal.prefixSelector.inputFields.fieldLabelPrefix')"
                                        data-qa="prefixInput"
                                        :maxlength="7"
                                        submitted
                                        :invalid="!isAlphanumeric(customPrefixInput)"
                                    >
                                        <template #help>
                                            <div class="input-field-text-container">
                                                <div>
                                                    {{ $t('email.auth.createModal.prefixSelector.inputFields.helpSubdomain') }}
                                                </div>

                                                <div>
                                                    {{ customPrefixInput.length }}/7
                                                </div>
                                            </div>
                                        </template>

                                        <template #error>
                                            <div class="input-field-text-container">
                                                <div>
                                                    {{ $t('email.auth.createModal.prefixSelector.error.characterError') }}
                                                </div>

                                                <div>
                                                    {{ customPrefixInput.length }}/7
                                                </div>
                                            </div>
                                        </template>
                                    </DsInputField>
                                </div>
                            </div>
                        </template>
                    </DsCollapsible>
                </DsCard>
            </div>

            <div class="record-table">
                <DsTableGrid :data="tableRecords">
                    <DsTableGridColumn
                        :label="$t('email.auth.createModal.labels.record')"
                        prop="recordNumber"
                        width="20"
                    />

                    <DsTableGridColumn
                        :label="$t('email.auth.createModal.labels.type')"
                        prop="recordType"
                        width="20"
                    />

                    <DsTableGridColumn
                        :label="$t('email.auth.createModal.labels.host')"
                    >
                        <template #default="{ host }">
                            <span class="copy-input" disabled @click="copy(host)">{{ host }}.{{ domain.domain }}<DsIcon class="copy-icon" name="copy" /></span>
                        </template>
                    </DsTableGridColumn>


                    <DsTableGridColumn
                        :label="$t('email.auth.createModal.labels.value')"
                    >
                        <template #default="{ value }">
                            <span class="copy-input" @click="copy(value)">{{ value }}<DsIcon class="copy-icon" name="copy" /></span>
                        </template>
                    </DsTableGridColumn>
                </DsTableGrid>
            </div>
        </section>

        <!-- eslint-disable vue/no-v-html -->
        <p v-if="showLearn" v-html="$t('email.auth.createModal.learn', { link: learn })" />
        <!-- eslint-enable vue/no-v-html -->

        <div class="actions">
            <DsFilledButton
                v-if="editing"
                :disabled="!isAlphanumeric(customPrefixInput)"
                class="close-button"
                @click="confirm"
            >
                {{ $t('global.saveChanges') }}
            </DsFilledButton>

            <DsFilledButton
                v-else
                class="confirm-button"
                @click="confirm"
            >
                {{ $t('global.finish') }}
            </DsFilledButton>
        </div>
    </div>
</template>

<script>
import {
    DKIM_PROVIDER_OTHER_ID,
    EMAIL_AUTH_TYPE_LEGACY,
    EMAIL_AUTH_TYPE_SPF,
    EMAIL_AUTH_TYPE_S1,
    EMAIL_AUTH_TYPE_S2,
    EMAIL_AUTH_TYPE_DMARC,
} from '@/components/settings/settings.constants';
import axios from 'axios';

export default {
    emits: ['close', 'refresh-email-auth'],

    props: {
        appId: {
            type: String,
            default: '',
        },
        domains: {
            type: Array,
            default: () => [],
        },
        domain: {
            type: Object,
            default: () => {},
        },
        environment: {
            type: String,
            default: '',
        },
        provider: {
            type: Object,
            default: () => {},
        },
        dmarc: {
            type: Object,
            default: null,
        },
        isCustomPrefixSelectorEnabled: {
            type: Boolean,
            default: false,
        },
        isUpdateDmarcContentEnabled: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            customPrefixInput: '',
            collapsibleOpen: false,
        };
    },

    computed: {
        chevronIcon() {
            return this.collapsibleOpen ? 'chevron-up' : 'chevron-down';
        },

        prefixSelector() {
            const selectorPrefix = this.customPrefixInput.length > 0 ? `${this.appId}-${this.customPrefixInput}` : this.appId;

            return selectorPrefix;
        },

        providerName() {
            return this.provider ? this.provider.name : '';
        },

        providerUrl() {
            return this.provider ? this.provider.url : '';
        },

        showLearn() {
            return this.provider && this.provider.id !== DKIM_PROVIDER_OTHER_ID;
        },

        learn() {
            return `<a href="${this.providerUrl}" target="_blank" rel="noopener noreferrer">${this.providerName}</a>`;
        },

        editing() {
            const records = this.domains
                .filter(({ domain, emailAuthType }) => {
                    return domain === this.domain?.domain
                    && emailAuthType
                    && emailAuthType !== EMAIL_AUTH_TYPE_LEGACY;
                });

            return this.dnsRecords.every(({ emailAuthType: expectedEmailAuthType }) => {
                return records.some(({ emailAuthType: recordEmailAuthType, id }) => expectedEmailAuthType === recordEmailAuthType && id > 0);
            });
        },

        tableRecords() {
            const tableRecords = [];

            const SPF_RECORD = {
                recordNumber: 1,
                recordType: 'CNAME',
                name: this.prefixSelector,
                host: this.prefixSelector,
                emailAuthType: EMAIL_AUTH_TYPE_SPF,
                value: this.cnameHosts.EMAIL_AUTH_TYPE_SPF,
            };

            tableRecords.push(SPF_RECORD);

            const S1_RECORD = {
                recordNumber: 2,
                recordType: 'CNAME',
                name: `${this.prefixSelector}1`,
                host: `${this.prefixSelector}1._domainkey`,
                emailAuthType: EMAIL_AUTH_TYPE_S1,
                value: this.cnameHosts.EMAIL_AUTH_TYPE_S1,
            };

            tableRecords.push(S1_RECORD);

            const S2_RECORD = {
                recordNumber: 3,
                recordType: 'CNAME',
                name: `${this.prefixSelector}2`,
                host: `${this.prefixSelector}2._domainkey`,
                emailAuthType: EMAIL_AUTH_TYPE_S2,
                value: this.cnameHosts.EMAIL_AUTH_TYPE_S2,
            };

            tableRecords.push(S2_RECORD);

            if (this.dmarc) {
                const DMARC_RECORD = {
                    recordNumber: 4,
                    recordType: 'TXT',
                    name: '_dmarc',
                    host: '_dmarc',
                    emailAuthType: EMAIL_AUTH_TYPE_DMARC,
                    value: this.generateDmarcRecord(this.dmarc),
                };

                tableRecords.push(DMARC_RECORD);
            }

            return tableRecords;
        },

        dnsRecords() {
            const dnsRecords = [];

            const SPF_RECORD = {
                recordNumber: 1,
                recordType: 'CNAME',
                name: this.prefixSelector,
                host: this.prefixSelector,
                emailAuthType: EMAIL_AUTH_TYPE_SPF,
                value: this.cnameHosts.EMAIL_AUTH_TYPE_SPF,
            };

            dnsRecords.push(SPF_RECORD);

            const S1_RECORD = {
                recordNumber: 2,
                recordType: 'CNAME',
                name: `${this.prefixSelector}1`,
                host: `${this.prefixSelector}1._domainkey`,
                emailAuthType: EMAIL_AUTH_TYPE_S1,
                value: this.cnameHosts.EMAIL_AUTH_TYPE_S1,
            };

            dnsRecords.push(S1_RECORD);

            const S2_RECORD = {
                recordNumber: 3,
                recordType: 'CNAME',
                name: `${this.prefixSelector}2`,
                host: `${this.prefixSelector}2._domainkey`,
                emailAuthType: EMAIL_AUTH_TYPE_S2,
                value: this.cnameHosts.EMAIL_AUTH_TYPE_S2,
            };

            dnsRecords.push(S2_RECORD);

            if (this.dmarc) {
                const DMARC_RECORD = {
                    recordNumber: 4,
                    recordType: 'TXT',
                    name: '_dmarc',
                    host: '_dmarc',
                    emailAuthType: EMAIL_AUTH_TYPE_DMARC,
                    value: this.generateDmarcRecord(this.dmarc),
                };

                dnsRecords.push(DMARC_RECORD);
            } else {
                const DMARC_RECORD = {
                    recordNumber: 4,
                    recordType: 'TXT',
                    name: '_dmarc',
                    host: '_dmarc',
                    emailAuthType: EMAIL_AUTH_TYPE_DMARC,
                    value: this.isUpdateDmarcContentEnabled ? this.generateDmarcRecord({ policy: 'quarantine', pct: 5 }) : this.generateDmarcRecord({ policy: 'none' }),
                };

                dnsRecords.push(DMARC_RECORD);
            }

            return dnsRecords;
        },

        cnameHosts() {
            let environmentSeparator = '';

            switch (this.environment.toLowerCase()) {
            case 'integration':
                environmentSeparator = 'intg.';
                break;
            case 'staging':
                environmentSeparator = 'stge.';
                break;
            case 'development':
                environmentSeparator = 'intg.';
                break;
            default:
                environmentSeparator = '';
            }

            return {
                EMAIL_AUTH_TYPE_SPF: `return.${environmentSeparator}infusionmail.com`,
                EMAIL_AUTH_TYPE_S1: `s1.domainkey.${environmentSeparator}infusionmail.com`,
                EMAIL_AUTH_TYPE_S2: `s2.domainkey.${environmentSeparator}infusionmail.com`,
            };
        },

    },
    methods: {
        isAlphanumeric(str) {
            return /^[a-zA-Z0-9]*$/.test(str);
        },

        generateDmarcRecord(dmarc) {
            let record = `v=DMARC1; p=${dmarc.policy};`;

            if (dmarc.pct !== null && dmarc.policy !== 'none') {
                record += ` pct=${dmarc.pct};`;
            }

            if (this.isUpdateDmarcContentEnabled) {
                if (dmarc.rua?.length && dmarc.policy !== 'none') {
                    record += ` rua=mailto:${dmarc.rua};`;
                }
            }

            return record;
        },

        copy(value) {
            const successHeadline = this.$t('email.auth.createModal.copy.successHeadline');
            const successMessage = this.$t('email.auth.createModal.copy.successMessage', { value });

            return this.$copyText(value).then(() => {
                this.$toast({
                    headline: successHeadline,
                    message: successMessage,
                });
            });
        },

        close() {
            this.$emit('close');
        },

        refreshEmailAuth() {
            this.$emit('refresh-email-auth');
        },

        async confirm() {
            if (!this.isAlphanumeric(this.customPrefixInput)) {
                return;
            }

            const domainsToCreate = this.dnsRecords
                .filter((expectedRecord) => !this.domains.some((existingRecord) => this.domain.domain === existingRecord.domain
                        && expectedRecord.emailAuthType === existingRecord.emailAuthType
                        && expectedRecord.host === existingRecord.dkimName
                        && existingRecord.id > 0))
                .map(({ name, host, emailAuthType }) => {
                    return {
                        domainId: name,
                        domain: this.domain.domain,
                        dkimName: host,
                        emailAuthType,
                    };
                });

            const mapIdsToDelete = this.domains
                .filter((existingRecord) => domainsToCreate.some((recordToCreate) => recordToCreate.domain === existingRecord.domain))
                .filter((existingRecord) => domainsToCreate.some((recordToCreate) => recordToCreate.emailAuthType === existingRecord.emailAuthType))
                .filter((existingRecord) => existingRecord.id > 0)
                .map(({ id }) => id.toString());

            const deletePromises = mapIdsToDelete.map((id) => {
                return axios.post(`/app/emailAuthorization/${id}/deleteDKIMSetting`);
            });

            const promiseToCreateAfterDelete = Promise.all(deletePromises)
                .then(async () => {
                    const createPromises = domainsToCreate.map((dnsRecord) => {
                        return axios.post('/app/emailAuthorization/create', dnsRecord);
                    });

                    return Promise.all(createPromises);
                })
                .catch(async () => {
                    await this.refreshEmailAuth();
                    this.$error({ message: this.$t('email.auth.domainDeleteError') });
                    await this.close();
                });

            await promiseToCreateAfterDelete
                .then(async () => {
                    await this.refreshEmailAuth();
                })
                .catch(async () => {
                    await this.refreshEmailAuth();
                    this.$error({ message: this.$t('email.auth.domainCreateError') });
                });

            await this.close();
        },

        toggleCollapsible() {
            this.collapsibleOpen = !this.collapsibleOpen;
        },
    },
};

</script>

<style lang="scss" rel="stylesheet/scss" type="test/scss" scoped>
.custom-dkim-collapsible {
    --collapsible-header-padding: #{$spacing-200};
}

.prefix-body {
    margin: $spacing-200 0;
}

.dkim-selector-card-container {
    width: px-to-rem(700);
    padding: $spacing-300 0;
}

.collapsible-header-container {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.input-field-container {
    display: flex;
    justify-content: space-between;
    gap: $spacing-200;
    margin-top: $spacing-200;
}

.input-field-text-container {
    display: flex;
    justify-content: space-between;
}

.container {
    display: flex;
    flex-direction: column;
    max-width: px-to-rem(1200);
    margin: 0 auto;
}

.secondary-title {
    margin-bottom: $spacing-100;
}

.instructions {
    margin-left: $spacing-200;
    padding: $spacing-100;
}

.record-table {
    overflow-x: auto;
    white-space: nowrap;
    padding-bottom: $spacing-100;
}

.copy-input {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    background-color: $color-gray-100;
    border: 1px solid $color-gray-200;
    padding: $spacing-100 $spacing-200;
    border-radius: $spacing-100;
    text-wrap: pretty;
}

@media($small) {
    .container {
        width: 100%;
    }

    .copy-input {
        text-wrap: unset;
    }
}

.copy-input:hover {
    cursor: pointer;
}

.copy-icon {
    margin-left: $spacing-100;
    color: $color-ink-900;
}

</style>
